/**
 * EmailActionResultPage - Reusable result page for email action links
 * 
 * Shows success, expired, or error states with calm UI.
 * ARC Doctrine: No urgency, no blame, no exclamation marks.
 */
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  Home,
  LogIn,
  RefreshCw,
  Users,
  ArrowRight
} from "lucide-react";

const STATUS_CONFIG = {
  success: {
    icon: CheckCircle2,
    iconColor: "text-emerald-500",
    bgColor: "bg-emerald-50 dark:bg-emerald-950/30",
    borderColor: "border-emerald-200 dark:border-emerald-800"
  },
  expired: {
    icon: Clock,
    iconColor: "text-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-950/30",
    borderColor: "border-amber-200 dark:border-amber-800"
  },
  error: {
    icon: AlertCircle,
    iconColor: "text-slate-400",
    bgColor: "bg-slate-50 dark:bg-slate-900/50",
    borderColor: "border-slate-200 dark:border-slate-700"
  },
  loading: {
    icon: RefreshCw,
    iconColor: "text-blue-500 animate-spin",
    bgColor: "bg-blue-50 dark:bg-blue-950/30",
    borderColor: "border-blue-200 dark:border-blue-800"
  },
  needs_auth: {
    icon: LogIn,
    iconColor: "text-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-950/30",
    borderColor: "border-blue-200 dark:border-blue-800"
  },
  already_handled: {
    icon: CheckCircle2,
    iconColor: "text-slate-400",
    bgColor: "bg-slate-50 dark:bg-slate-900/50",
    borderColor: "border-slate-200 dark:border-slate-700"
  }
};

export function EmailActionResultPage({
  status = "loading",
  title,
  message,
  primaryButtonLabel,
  primaryButtonAction,
  secondaryButtonLabel,
  secondaryButtonAction,
  showHomeButton = true,
  contextInfo = null
}) {
  const navigate = useNavigate();
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.error;
  const Icon = config.icon;

  const handlePrimaryClick = () => {
    if (primaryButtonAction) {
      if (typeof primaryButtonAction === "string") {
        navigate(primaryButtonAction);
      } else {
        primaryButtonAction();
      }
    }
  };

  const handleSecondaryClick = () => {
    if (secondaryButtonAction) {
      if (typeof secondaryButtonAction === "string") {
        navigate(secondaryButtonAction);
      } else {
        secondaryButtonAction();
      }
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4"
      data-testid="email-action-result-page"
    >
      <Card className={`max-w-md w-full ${config.borderColor}`}>
        <CardContent className="p-8">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-full ${config.bgColor} flex items-center justify-center mx-auto mb-6`}>
            <Icon className={`w-8 h-8 ${config.iconColor}`} />
          </div>

          {/* Title */}
          <h1 
            className="text-xl font-semibold text-center text-foreground mb-3"
            data-testid="result-title"
          >
            {title || getDefaultTitle(status)}
          </h1>

          {/* Message */}
          <p 
            className="text-center text-muted-foreground mb-6"
            data-testid="result-message"
          >
            {message || getDefaultMessage(status)}
          </p>

          {/* Context Info */}
          {contextInfo && (
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 mb-6" data-testid="context-info">
              {contextInfo.household_name && (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Household: <span className="font-medium text-foreground">{contextInfo.household_name}</span>
                </p>
              )}
              {contextInfo.inviter_name && (
                <p className="text-sm text-muted-foreground mt-1">
                  Invited by: <span className="font-medium text-foreground">{contextInfo.inviter_name}</span>
                </p>
              )}
              {contextInfo.action_description && (
                <p className="text-sm text-muted-foreground mt-1">
                  Change: <span className="font-medium text-foreground">{contextInfo.action_description}</span>
                </p>
              )}
              {contextInfo.requester_name && (
                <p className="text-sm text-muted-foreground mt-1">
                  Requested by: <span className="font-medium text-foreground">{contextInfo.requester_name}</span>
                </p>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-3">
            {primaryButtonLabel && (
              <Button
                onClick={handlePrimaryClick}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="primary-button"
              >
                {primaryButtonLabel}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}

            {secondaryButtonLabel && (
              <Button
                variant="outline"
                onClick={handleSecondaryClick}
                className="w-full"
                data-testid="secondary-button"
              >
                {secondaryButtonLabel}
              </Button>
            )}

            {showHomeButton && !primaryButtonLabel && status !== "loading" && (
              <Button
                onClick={() => navigate("/")}
                className="w-full"
                data-testid="home-button"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            )}
          </div>

          {/* Footer */}
          <p className="text-xs text-center text-muted-foreground mt-6">
            ARA Wealth - A household becoming wealthy together
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Default titles and messages (ARC Doctrine compliant)
function getDefaultTitle(status) {
  switch (status) {
    case "success":
      return "All done";
    case "expired":
      return "This link has expired";
    case "error":
      return "Something went wrong";
    case "loading":
      return "Processing your request";
    case "needs_auth":
      return "Sign in to continue";
    case "already_handled":
      return "Already processed";
    default:
      return "Action complete";
  }
}

function getDefaultMessage(status) {
  switch (status) {
    case "success":
      return "Your action has been completed successfully.";
    case "expired":
      return "This link is no longer valid. You can request a new one from the app.";
    case "error":
      return "We could not complete this action. Please try again from the app.";
    case "loading":
      return "Please wait a moment while we process your request.";
    case "needs_auth":
      return "Please sign in or create an account to continue.";
    case "already_handled":
      return "This action has already been completed.";
    default:
      return "";
  }
}

export default EmailActionResultPage;
